import { useCallback, useEffect, useRef, useState } from "react";
import { LayoutDashboard, FileText, ArrowLeftRight, Landmark, Receipt, ShoppingCart, BookOpen, Settings, Gauge, Send, Store, Plus, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AccountingHome from "./AccountingHome";
import AccountingExpenses from "./AccountingExpenses";
import AccountingBanking from "./AccountingBanking";
import AccountingPayments from "./AccountingPayments";
import AccountingSettings from "./AccountingSettings";
import AccountingInvoices from "./AccountingInvoices";
import AccountingRecords from "./AccountingRecords";
import PersonalExpenseDialog from "./PersonalExpenseDialog";
import { rangeBounds, inputCls, todayIso } from "./accountingUtils";

const MENU = [
  { key: "home", label: "Overview", Icon: LayoutDashboard },
  { key: "invoices", label: "Invoices & customers", Icon: FileText },
  { key: "expenses", label: "Expenses", Icon: ArrowLeftRight },
  { key: "banking", label: "Banking", Icon: Landmark },
  { key: "payments", label: "Send money", Icon: Send, companyOnly: true },
  { key: "suppliers", label: "Suppliers", Icon: Store },
  { key: "snap", label: "Receipt snap", Icon: Receipt },
  { key: "items", label: "Products & services", Icon: ShoppingCart },
  { key: "accounts", label: "Chart of accounts", Icon: BookOpen },
  { key: "mileage", label: "Mileage", Icon: Gauge },
  { key: "settings", label: "Settings", Icon: Settings },
];
const RECORD_VIEWS = ["suppliers", "items", "accounts", "mileage"];

/**
 * AccountingTab — the in-app QuickBooks. Books never mix:
 *   scope="personal" → Home → Accounting (icon) sub-tab, every employee's own books
 *                      (owners/superadmin may view others read-only; superadmin
 *                      also gets three extra named ledgers 1 · 2 · 3)
 *   scope="company"  → main Accounting tab (top icon), shared company books
 */
const AccountingTab = ({ scope: rootScope = "personal" }) => {
  const { user } = useAuth();
  const isOwner = ["owner", "superadmin"].includes(user?.role);
  const isSuper = user?.role === "superadmin";
  const [ledgers, setLedgers] = useState([]);
  const [scope, setScopeState] = useState(() => { try { return (rootScope === "personal" && sessionStorage.getItem("accounting_ledger")) || rootScope; } catch { return rootScope; } });
  const setScope = (s) => { try { sessionStorage.setItem("accounting_ledger", s); } catch { /* private */ } setScopeState(s); setView("home"); };
  const base = `/accounting/${scope}`;
  const bankBase = `/banking/${scope}`;
  const VIEW_KEY = `accounting_view_${scope}`;
  const canViewOthers = scope === "personal" && isOwner;
  const canPay = rootScope === "company" && isOwner;
  const [view, setViewState] = useState(() => { try { return sessionStorage.getItem(VIEW_KEY) || "home"; } catch { return "home"; } });
  const setView = (v) => { try { sessionStorage.setItem(`accounting_view_${scope}`, v); } catch { /* private */ } setViewState(v); };

  const [viewUserId, setViewUserId] = useState("");
  const [people, setPeople] = useState([]);
  const readOnly = !!viewUserId && viewUserId !== user?.user_id;

  const [settings, setSettings] = useState({ default_tax_rate: 13, name: "" });
  const [filters, setFilters] = useState({ q: "", sort: "newest", range: "12m", category: "" });
  const [expenses, setExpenses] = useState([]);
  const [monthExpenses, setMonthExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bank, setBank] = useState({ items: [], new_transactions: 0 });
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null, fromTx: null });
  const [fab, setFab] = useState(false);
  const [newSale, setNewSale] = useState(null);
  const nameTimer = useRef(null);

  const loadLedgers = useCallback(() => { if (rootScope === "personal" && isSuper) api.get("/accounting/personal/ledgers").then((r) => setLedgers(r.data || [])).catch(() => {}); }, [rootScope, isSuper]);
  useEffect(() => { loadLedgers(); }, [loadLedgers]);
  useEffect(() => {
    api.get(`${base}/settings`).then((r) => setSettings(r.data)).catch(() => {});
    if (canViewOthers) api.get(`${base}/people`).then((r) => setPeople(r.data || [])).catch(() => {});
  }, [base, canViewOthers]);

  const loadBank = useCallback(async () => {
    try { const { data } = await api.get(`${bankBase}/items`, { params: { user_id: viewUserId || undefined } }); setBank(data); } catch { /* optional */ }
  }, [bankBase, viewUserId]);
  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get(`${base}/summary`, { params: { user_id: viewUserId || undefined } }); setSummary(data); } catch { /* optional */ }
  }, [base, viewUserId]);

  const load = useCallback(async () => {
    setLoading(true);
    const uid = viewUserId || undefined;
    try {
      const [list, month] = await Promise.all([
        api.get(`${base}/expenses`, { params: { user_id: uid, sort: filters.sort, category: filters.category || undefined, q: filters.q || undefined, ...rangeBounds(filters.range) } }),
        api.get(`${base}/expenses`, { params: { user_id: uid, ...rangeBounds("month") } }),
      ]);
      setExpenses(list.data.expenses || []);
      setMonthExpenses(month.data.expenses || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load accounting");
    } finally {
      setLoading(false);
    }
  }, [base, viewUserId, filters]);

  useEffect(() => { const t = setTimeout(load, filters.q ? 300 : 0); return () => clearTimeout(t); }, [load, filters.q]);
  useEffect(() => { loadBank(); loadSummary(); }, [loadBank, loadSummary]);

  const refreshAll = () => { load(); loadBank(); loadSummary(); };
  const openNew = () => setDialog({ open: true, initial: null, fromTx: null });
  const openFromTx = (tx) => setDialog({ open: true, fromTx: tx, initial: { date: tx.date || todayIso(), payee: tx.name, payment_method: "debit", lines: [{ amount: Math.abs(tx.amount), category: tx.category || "", who_for: "", description: "" }], tax_mode: "none" } });
  const onMenu = (m) => {
    if (m.key === "snap") return readOnly ? toast.info("Switch back to your own books to add expenses") : openNew();
    setView(m.key);
  };
  const saveName = (name) => {
    setSettings((s) => ({ ...s, name }));
    clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => api.put(`${base}/settings`, { name }).then(loadLedgers).catch(() => toast.error("Could not save the name")), 500);
  };
  const menu = MENU.filter((m) => !m.companyOnly || canPay);
  const isLedger = scope.startsWith("book");
  const title = isLedger ? (settings.name || `Ledger ${scope.slice(-1)}`) : rootScope === "company" ? "Company accounting" : "My accounting";

  return (
    <div className="relative min-w-0 space-y-4 pb-20" data-testid={`accounting-tab-${rootScope}`}>
      {/* superadmin: own books + three extra ledgers */}
      {rootScope === "personal" && isSuper && (
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="acct-ledgers">
          <button type="button" onClick={() => setScope("personal")} data-testid="acct-ledger-personal" className={`h-9 shrink-0 rounded-full border px-3 font-mono text-[11px] uppercase tracking-widest ${scope === "personal" ? "border-[#1a3fd1] bg-[#1a3fd1] text-white" : "border-white/15 text-white/60 hover:text-white"}`}>Mine</button>
          {ledgers.map((l) => (
            <button key={l.scope} type="button" onClick={() => setScope(l.scope)} data-testid={`acct-ledger-${l.scope}`} title={l.name || `Ledger ${l.index}`} className={`flex h-9 shrink-0 items-center gap-2 rounded-full border px-3 font-mono text-[11px] uppercase tracking-widest ${scope === l.scope ? "border-[#1a3fd1] bg-[#1a3fd1] text-white" : "border-white/15 text-white/60 hover:text-white"}`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[10px] font-bold">{l.index}</span>
              <span className="max-w-[9rem] truncate normal-case tracking-normal">{l.name || `Ledger ${l.index}`}</span>
            </button>
          ))}
        </div>
      )}

      {/* title (editable for the extra ledgers) */}
      {isLedger ? (
        <input value={settings.name || ""} onChange={(e) => saveName(e.target.value)} placeholder={`Ledger ${scope.slice(-1)} — type a name`} maxLength={40} data-testid="acct-ledger-name" className="w-full bg-transparent text-2xl font-bold tracking-tight text-white placeholder:text-white/30 focus:outline-none" />
      ) : (
        <h2 className="text-lg font-bold tracking-tight text-white" data-testid="acct-title">{title}</h2>
      )}

      {/* Icon menu */}
      <div className="flex gap-1.5 overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02] p-1.5" role="tablist" aria-label="Accounting menu" data-testid="acct-menu">
        {menu.map((m) => {
          const active = view === m.key;
          return (
            <button key={m.key} type="button" role="tab" aria-selected={active} aria-label={m.label} title={m.label} onClick={() => onMenu(m)} data-testid={`acct-menu-${m.key}`}
              className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors ${active ? "bg-[#1a3fd1] text-white shadow" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <m.Icon className="h-[18px] w-[18px]" />
              {m.key === "banking" && bank.new_transactions > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-none text-black" data-testid="acct-menu-banking-badge">{bank.new_transactions > 99 ? "99+" : bank.new_transactions}</span>}
            </button>
          );
        })}
      </div>

      {canViewOthers && people.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Books</span>
          <select value={viewUserId} onChange={(e) => setViewUserId(e.target.value)} data-testid="acct-person" className={`${inputCls} h-9 w-auto bg-[#0a0a0a]`}>
            <option value="">My own</option>
            {people.filter((p) => p.user_id !== user?.user_id).map((p) => <option key={p.user_id} value={p.user_id}>{p.name || p.email}</option>)}
          </select>
          {readOnly && <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300" data-testid="acct-readonly">read only</span>}
        </div>
      )}

      {view === "expenses" ? (
        <AccountingExpenses base={base} expenses={expenses} loading={loading} filters={filters} setFilters={setFilters} readOnly={readOnly} onEdit={(e) => setDialog({ open: true, initial: e, fromTx: null })} onChanged={refreshAll} />
      ) : view === "invoices" ? (
        <AccountingInvoices base={base} readOnly={readOnly} viewUserId={viewUserId} defaultTaxRate={settings.default_tax_rate} onChanged={loadSummary} openNew={newSale} clearOpenNew={() => setNewSale(null)} />
      ) : RECORD_VIEWS.includes(view) ? (
        <AccountingRecords base={base} entity={view} readOnly={readOnly} viewUserId={viewUserId} onChanged={loadSummary} />
      ) : view === "banking" ? (
        <AccountingBanking base={bankBase} scope={scope} readOnly={readOnly} viewUserId={viewUserId} items={bank.items} onItemsChanged={loadBank} expenses={expenses} onAddExpenseFromTx={openFromTx} onExpensesChanged={load} />
      ) : view === "payments" && canPay ? (
        <AccountingPayments />
      ) : view === "settings" ? (
        <AccountingSettings base={base} settings={settings} onSaved={(s) => setSettings((p) => ({ ...p, ...s }))} />
      ) : (
        <AccountingHome base={bankBase} readOnly={readOnly} items={bank.items} newTx={bank.new_transactions} monthExpenses={monthExpenses} summary={summary} onOpenExpenses={() => setView("expenses")} onOpenBanking={() => { loadBank(); setView("banking"); }} onOpenInvoices={() => setView("invoices")} onOpenMileage={() => setView("mileage")} />
      )}

      {!readOnly && (
        <div className="fixed bottom-20 right-4 z-40 flex flex-col items-end gap-2 md:bottom-8 md:right-8">
          {fab && (
            <div className="flex flex-col items-end gap-2" data-testid="acct-fab-menu">
              {[["expense", "Expense"], ["invoice", "Invoice"], ["sales_receipt", "Sales receipt"], ["mileage", "Trip"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`acct-fab-${k}`} onClick={() => { setFab(false); if (k === "expense") openNew(); else if (k === "mileage") setView("mileage"); else { setView("invoices"); setNewSale(k); } }} className="rounded-full border border-white/15 bg-[#0b0b0f] px-4 py-2 text-xs font-semibold text-white shadow-lg hover:border-[#1a3fd1]">{l}</button>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setFab((f) => !f)} data-testid="acct-fab-add" aria-label="New" className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1a3fd1] text-white shadow-lg shadow-black/40 transition-transform hover:scale-105 active:scale-95">
            {fab ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
          </button>
        </div>
      )}

      <PersonalExpenseDialog base={base} bankBase={bankBase} fromTx={dialog.fromTx} open={dialog.open} initial={dialog.initial} defaultTaxRate={settings.default_tax_rate} onClose={() => setDialog({ open: false, initial: null, fromTx: null })} onSaved={refreshAll} />
    </div>
  );
};

export default AccountingTab;
